import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { api, apiErrorMessage, formatINR } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import CountUp from "@/components/CountUp";
import { useAuth } from "@/context/AuthContext";
import { DASH } from "@/constants/testIds";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Clock,
  Warning,
  CoinVertical,
  HandCoins,
  Handshake,
  Scales,
  Sparkle,
  Plus,
  TrendUp,
  CalendarBlank,
  Wallet,
} from "@phosphor-icons/react";

const cardMotion = {
  hidden: { opacity: 0, y: 12 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] },
  }),
};

function inrShort(n) {
  const num = Number(n || 0);
  const sign = num < 0 ? "-" : "";
  const a = Math.abs(num);
  if (a >= 10000000) return `${sign}₹${(a / 10000000).toFixed(2)} Cr`;
  if (a >= 100000) return `${sign}₹${(a / 100000).toFixed(2)} L`;
  if (a >= 1000) return `${sign}₹${(a / 1000).toFixed(0)}K`;
  return formatINR(num);
}

function KpiCard({ label, value, testId, icon, tint = "indigo", index = 0, subtitle }) {
  const tintClass = {
    indigo: "from-indigo-500/10",
    amber: "from-amber-500/10",
    rose: "from-rose-500/10",
    emerald: "from-emerald-500/10",
    fuchsia: "from-fuchsia-500/10",
    sky: "from-sky-500/10",
  }[tint];
  return (
    <motion.div
      variants={cardMotion}
      custom={index}
      initial="hidden"
      animate="show"
      whileHover={{ y: -4 }}
      data-testid={testId}
      className={`lele-card p-5 relative overflow-hidden bg-gradient-to-br ${tintClass} to-transparent`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          {label}
        </span>
        {icon}
      </div>
      <div className="mt-4 font-heading text-2xl md:text-[1.7rem] tracking-tight text-neutral-100">
        <CountUp value={value} formatter={(v) => inrShort(v)} />
      </div>
      {subtitle && (
        <div className="text-[11px] text-neutral-500 mt-1">{subtitle}</div>
      )}
    </motion.div>
  );
}

function StatusChip({ status }) {
  const cls =
    status === "recovered"
      ? "status-recovered"
      : status === "overdue"
      ? "status-overdue"
      : "status-pending";
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return <span className={`lele-chip ${cls}`}>{label}</span>;
}

function LoanRow({ loan, side, testId }) {
  return (
    <motion.li
      data-testid={testId}
      whileHover={{ x: 2 }}
      transition={{ duration: 0.15 }}
      className="px-6 py-4 border-b border-white/5 last:border-b-0 hover:bg-white/[0.025] transition-colors"
    >
      <Link to={`/loans/${loan.id}`} className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-neutral-100 font-medium truncate">
              {side === "borrower" ? `From ${loan.lender_name}` : loan.borrower_name || "Unnamed borrower"}
            </span>
            <StatusChip status={loan.computed_status} />
            {loan.consent_status === "awaiting" && (
              <span className="lele-chip status-awaiting">awaiting consent</span>
            )}
          </div>
          <div className="text-xs text-neutral-500 mt-1 truncate">
            Due {new Date(loan.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
            {loan.purpose ? ` · ${loan.purpose}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <div className="font-heading text-lg text-neutral-100">{formatINR(loan.amount)}</div>
            <div className="text-[11px] text-neutral-500">Repayable {formatINR(loan.total_repayable)}</div>
          </div>
          <ArrowUpRight size={18} weight="bold" className="text-neutral-500" />
        </div>
      </Link>
    </motion.li>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [summary, setSummary] = useState({
    total_receivable: 0,
    total_payable: 0,
    net_position: 0,
    active_loans: 0,
    overdue_loans: 0,
    upcoming_due: 0,
    lender: { count: 0 },
    borrower: { count: 0 },
  });
  const [loans, setLoans] = useState([]);
  const [borrowerLoans, setBorrowerLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("lender");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, ll, bl] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/loans"),
          api.get("/loans/borrower"),
        ]);
        setSummary(s.data);
        setLoans(ll.data);
        setBorrowerLoans(bl.data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const hasBorrowerLoans = borrowerLoans.length > 0 || summary.borrower.count > 0;
  const isBorrower = tab === "borrower";

  const list = isBorrower ? borrowerLoans : loans;

  const netPositive = (summary.net_position || 0) >= 0;

  const kpis = useMemo(
    () => [
      {
        label: t("dashboard.kpi.receivable"),
        value: summary.total_receivable,
        testId: "kpi-total-receivable",
        icon: <TrendUp size={18} weight="duotone" className="text-emerald-400" />,
        tint: "emerald",
        subtitle: t("dashboard.kpiHint.receivable", { n: summary.lender.active_count || 0 }),
      },
      {
        label: t("dashboard.kpi.payable"),
        value: summary.total_payable,
        testId: "kpi-total-payable",
        icon: <HandCoins size={18} weight="duotone" className="text-rose-400" />,
        tint: "rose",
        subtitle: t("dashboard.kpiHint.payable", { n: summary.borrower.active_count || 0 }),
      },
      {
        label: t("dashboard.kpi.active"),
        value: summary.active_loans,
        testId: "kpi-active-loans",
        icon: <CoinVertical size={18} weight="duotone" className="text-indigo-400" />,
        tint: "indigo",
        subtitle: t("dashboard.kpiHint.active"),
      },
      {
        label: t("dashboard.kpi.overdue"),
        value: summary.overdue_loans,
        testId: "kpi-overdue-loans",
        icon: <Warning size={18} weight="duotone" className="text-amber-400" />,
        tint: "amber",
        subtitle: t("dashboard.kpiHint.overdue"),
      },
      {
        label: t("dashboard.kpi.upcoming"),
        value: summary.upcoming_due,
        testId: "kpi-upcoming-due",
        icon: <CalendarBlank size={18} weight="duotone" className="text-sky-400" />,
        tint: "sky",
        subtitle: t("dashboard.kpiHint.upcoming"),
      },
      {
        label: t("dashboard.kpi.net"),
        value: summary.net_position,
        testId: "kpi-net-position",
        icon: (
          <Wallet
            size={18}
            weight="duotone"
            className={netPositive ? "text-emerald-400" : "text-rose-400"}
          />
        ),
        tint: netPositive ? "emerald" : "rose",
        subtitle: netPositive
          ? t("dashboard.kpiHint.netLender")
          : t("dashboard.kpiHint.netBorrower"),
      },
    ],
    [summary, netPositive, t]
  );

  const formatKpi = (kpi) =>
    ["kpi-active-loans", "kpi-overdue-loans"].includes(kpi.testId)
      ? { ...kpi, value: kpi.value, testId: kpi.testId, formatter: (v) => Math.round(v) }
      : kpi;

  return (
    <div data-testid={DASH.root} className="min-h-screen flex flex-col">
      <AppHeader />
      <main className="flex-1 mx-auto max-w-6xl px-5 md:px-8 py-8 md:py-12 w-full">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6"
        >
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-indigo-300 mb-2">
              {t("dashboard.yourLedger")}
            </div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">
              {t("dashboard.welcomeName", { name: user?.name?.split(" ")[0] || "" })}
            </h1>
            <p className="text-neutral-400 mt-2 text-sm">
              {t("dashboard.loanCountSelf", {
                lender: summary.lender.count,
                borrower: summary.borrower.count,
              })}
            </p>
          </div>
          <motion.button
            data-testid={DASH.newLoanBtn}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => nav("/new")}
            className="lele-btn-primary flex items-center gap-2 self-start md:self-auto relative overflow-hidden group"
          >
            <span
              className="pointer-events-none absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"
              style={{ background: "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.25) 45%, transparent 90%)" }}
            />
            <Sparkle size={16} weight="fill" />
            {t("dashboard.createWithAi")}
          </motion.button>
        </motion.div>

        {/* 6-metric KPI grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {kpis.map((k, i) => (
            <KpiCard key={k.label} {...formatKpi(k)} index={i} />
          ))}
        </div>

        {/* Tabs */}
        {(hasBorrowerLoans || loans.length > 0) && (
          <div className="mb-6 inline-flex p-1 rounded-full border border-white/10 bg-white/[0.02]">
            <TabBtn
              testId="dashboard-tab-lender"
              active={!isBorrower}
              onClick={() => setTab("lender")}
              icon={<Handshake size={14} weight="fill" />}
              label="As lender"
              count={summary.lender.count}
            />
            <TabBtn
              testId="dashboard-tab-borrower"
              active={isBorrower}
              onClick={() => setTab("borrower")}
              icon={<HandCoins size={14} weight="fill" />}
              label="As borrower"
              count={summary.borrower.count}
            />
          </div>
        )}

        {/* Loans list */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.15 }}
          className="lele-card p-0 overflow-hidden"
          data-testid={DASH.loansTable}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 flex items-center gap-2">
              <Scales size={13} weight="duotone" className="text-indigo-400" />
              {isBorrower ? "Loans you owe" : t("dashboard.recent")}
            </div>
          </div>

          {loading ? (
            <div className="p-10 text-neutral-500 text-sm">{t("common.loading")}</div>
          ) : list.length === 0 ? (
            <div className="p-12 text-center">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: "backOut" }}
                className="mx-auto h-12 w-12 rounded-full bg-indigo-500/10 grid place-items-center mb-4"
              >
                <Plus size={22} weight="bold" className="text-indigo-400" />
              </motion.div>
              <div className="font-heading text-xl mb-2">
                {isBorrower ? "You don't owe anyone here" : t("dashboard.empty.title")}
              </div>
              <p className="text-neutral-500 text-sm max-w-sm mx-auto">
                {isBorrower ? (
                  <>When someone documents a loan to you on LeLe using your phone number, it will show up here.</>
                ) : (
                  t("dashboard.empty.hint")
                )}
              </p>
              {!isBorrower && (
                <motion.button whileHover={{ scale: 1.03 }} onClick={() => nav("/new")} className="lele-btn-primary mt-6 inline-flex items-center gap-2">
                  <Sparkle size={16} weight="fill" />
                  {t("dashboard.empty.cta")}
                </motion.button>
              )}
            </div>
          ) : (
            <ul>
              {list.map((l) => (
                <LoanRow key={l.id} loan={l} side={isBorrower ? "borrower" : "lender"} testId={DASH.loanRow(l.id)} />
              ))}
            </ul>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, count, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      type="button"
      className={`flex items-center gap-1.5 text-xs font-medium py-2 px-4 rounded-full transition-colors ${
        active ? "bg-indigo-500/15 text-indigo-200 border border-indigo-400/40" : "text-neutral-400 hover:text-neutral-100"
      }`}
    >
      {icon}
      {label}
      <span
        className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
          active ? "bg-indigo-400/20 text-indigo-100" : "bg-white/[0.06] text-neutral-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
